import { useEffect, useMemo, useState } from "react";
// REDUX
import { useLazyGetDeletedBanksQuery } from "../../redux/banks/bankAPI";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// UTILS
import { DeletedBankRow, toDeletedBankRows } from "../../utils/deletedBanks";

/** Rows per page of the deleted-banks table; a delete is rare, so one page is the norm. */
export const DELETED_BANKS_PAGE_SIZE = 20;

/** What narrows the deleted list. `orgId` is required by the server. */
export interface DeletedBanksScope {
  orgId: string;
  /** Wizard: the server whose banks the step is editing. */
  serverId?: string;
  channelId?: string;
}

/**
 * The soft-deleted banks of one scope, fetched only while `enabled`.
 *
 * Both restore surfaces sit behind a collapsed section, and a deleted bank is
 * the rare case, so nothing is requested until the section is opened. The
 * request is repeated on every open and whenever the scope or page changes,
 * because the API slice keeps no unused data (`keepUnusedDataFor: 0`) and a
 * list read an hour ago would offer banks somebody has since restored. After a
 * delete or a restore the slice's tags refresh a list that is already open.
 *
 * The page resets to 1 when the scope changes. That is derived rather than done
 * in an effect: an effect would fetch the stale page of the new scope first and
 * then the right one.
 */
export default function useDeletedBanks(
  scope: DeletedBanksScope,
  enabled: boolean,
): {
  rows: DeletedBankRow[];
  /** The server's total across every page, for the header count and the pager. */
  total: number;
  /** True once the list has answered, so "0 deleted banks" is never shown during load. */
  loaded: boolean;
  /**
   * True while a request is in flight or none has answered yet. A failed first
   * request is not "still loading": the error is what there is to show.
   */
  loading: boolean;
  /** The RTK Query error from the last request, if it failed. */
  error: unknown;
  page: number;
  setPage: (page: number) => void;
} {
  const { orgId, serverId, channelId } = scope;
  const scopeKey = `${orgId}|${serverId ?? ""}|${channelId ?? ""}`;
  const [paging, setPaging] = useState({ scopeKey, page: 1 });
  const page = paging.scopeKey === scopeKey ? paging.page : 1;

  const [triggerGetDeletedBanks, { data, isFetching, error }] = useLazyGetDeletedBanksQuery();

  useEffect(() => {
    if (!enabled) return;
    const load = async () => {
      const token = await GETJwtToken();
      triggerGetDeletedBanks({
        orgId,
        serverId,
        channelId,
        page,
        limit: DELETED_BANKS_PAGE_SIZE,
        token,
      });
    };
    load();
  }, [enabled, scopeKey, page]);

  const { rows, total } = useMemo(() => toDeletedBankRows(data), [data]);
  const loaded = data !== undefined;
  const setPage = (next: number) => setPaging({ scopeKey, page: next });

  // Restoring the last row of a later page leaves that page empty. Step back to
  // the last page that still has rows instead of showing an empty table.
  useEffect(() => {
    if (loaded && !isFetching && page > 1 && rows.length === 0) {
      setPage(Math.max(1, Math.ceil(total / DELETED_BANKS_PAGE_SIZE)));
    }
  }, [loaded, isFetching, page, rows.length, total]);

  const loading = isFetching || (!loaded && !error);

  return { rows, total, loaded, loading, error, page, setPage };
}
